/**
 * EventNotificationButton Component
 * イベント開始30分前通知の登録/解除ボタン
 * 状態は親コンポーネントで一元管理される
 */

import { useTranslation } from 'react-i18next';

interface EventNotificationButtonProps {
  isSubscribed: boolean;
  isLoading: boolean;
  onToggle: () => void;
  message?: { type: 'success' | 'error'; text: string } | null;
  className?: string;
}

export default function EventNotificationButton({
  isSubscribed,
  isLoading,
  onToggle,
  message,
  className = '',
}: EventNotificationButtonProps) {
  const { t } = useTranslation();

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <button
        onClick={onToggle}
        disabled={isLoading}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
          transition-colors duration-200 disabled:opacity-50
          ${
            isSubscribed
              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-300'
              : 'bg-blue-100 text-blue-800 hover:bg-blue-200 border border-blue-300'
          }
        `}
        title={
          isSubscribed
            ? t('eventNotification.unsubscribeTooltip', 'クリックして通知を解除')
            : t('eventNotification.subscribeTooltip', 'クリックして通知を受け取る')
        }
      >
        {isLoading ? (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : isSubscribed ? (
          <svg
            className="h-4 w-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
          </svg>
        ) : (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
        )}
        <span>
          {isSubscribed
            ? t('eventNotification.unsubscribe', '通知を解除')
            : t('eventNotification.subscribe', '通知を受け取る')}
        </span>
      </button>
      {message?.type === 'error' && (
        <p className="text-xs text-red-600 mt-1">{message.text}</p>
      )}
      {message?.type === 'success' && (
        <p className="text-xs text-green-600 mt-1">{message.text}</p>
      )}
    </div>
  );
}
